import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDebounce } from './useDebounce';

export interface JobPostingFilters {
  searchInput: string;
  debouncedSearch: string;
  status: string;
  role: string;
  siteState: string;
  dateFrom: string;
  dateTo: string;
  page: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  activeFilterCount: number;
  setSearchInput: (value: string) => void;
  setStatus: (value: string) => void;
  setRole: (value: string) => void;
  setSiteState: (value: string) => void;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  setPage: (page: number) => void;
  handleSort: (field: string) => void;
  clearAll: () => void;
}

export function useJobPostingFilters(): JobPostingFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebounce(searchInput, 300);

  // Sync debounced search value to URL and reset page
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set('q', debouncedSearch);
        } else {
          next.delete('q');
        }
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setSearchParams]);

  // Sync searchInput from URL when q param changes externally
  useEffect(() => {
    const currentQ = searchParams.get('q') ?? '';
    if (currentQ !== debouncedSearch) {
      setSearchInput(currentQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const status = searchParams.get('status') ?? '';
  const role = searchParams.get('role') ?? '';
  const siteState = searchParams.get('siteState') ?? '';
  const dateFrom = searchParams.get('from') ?? '';
  const dateTo = searchParams.get('to') ?? '';
  const page = Number(searchParams.get('page') ?? '1');
  const sortBy = searchParams.get('sortBy') ?? '';
  const sortOrder = (searchParams.get('sortOrder') ?? 'desc') as 'asc' | 'desc';

  const updateParamAndResetPage = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          updater(next);
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setStatus = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('status', value);
        } else {
          p.delete('status');
        }
      }),
    [updateParamAndResetPage],
  );

  const setRole = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('role', value);
        } else {
          p.delete('role');
        }
      }),
    [updateParamAndResetPage],
  );

  const setSiteState = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('siteState', value);
        } else {
          p.delete('siteState');
        }
      }),
    [updateParamAndResetPage],
  );

  const setDateFrom = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('from', value);
        } else {
          p.delete('from');
        }
      }),
    [updateParamAndResetPage],
  );

  const setDateTo = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('to', value);
        } else {
          p.delete('to');
        }
      }),
    [updateParamAndResetPage],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newPage <= 1) {
            next.delete('page');
          } else {
            next.set('page', String(newPage));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSort = useCallback(
    (field: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const currentSortBy = next.get('sortBy') ?? '';
          const currentSortOrder = next.get('sortOrder') ?? 'desc';
          if (currentSortBy === field) {
            next.set('sortOrder', currentSortOrder === 'asc' ? 'desc' : 'asc');
          } else {
            next.set('sortBy', field);
            next.set('sortOrder', 'asc');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchInput('');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (debouncedSearch) count++;
    if (status) count++;
    if (role) count++;
    if (siteState) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [debouncedSearch, status, role, siteState, dateFrom, dateTo]);

  return {
    searchInput,
    debouncedSearch,
    status,
    role,
    siteState,
    dateFrom,
    dateTo,
    page,
    sortBy,
    sortOrder,
    activeFilterCount,
    setSearchInput,
    setStatus,
    setRole,
    setSiteState,
    setDateFrom,
    setDateTo,
    setPage,
    handleSort,
    clearAll,
  };
}
